import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { FileText, MessageCircle, CheckSquare, Users, MoreHorizontal, BookOpen, Paperclip, BarChart3, Settings, X } from 'lucide-react'
import { colors } from '../styles/tokens'
import { useAuth } from '../hooks/useAuth'
import { useTeamUnread } from '../hooks/useTeamUnread'

const API_URL = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm'

export default function BottomTabs() {
  const navigate = useNavigate()
  const location = useLocation()
  const { profile } = useAuth()
  const { totalUnread: teamBadge } = useTeamUnread(profile?.id)
  const [taskBadge, setTaskBadge] = useState(0)
  const [showMore, setShowMore] = useState(false)

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const resp = await fetch(`${API_URL}/tasks/counts?seller_id=default`)
        if (resp.ok) {
          const data = await resp.json()
          setTaskBadge(data.active || 0)
        }
      } catch (e) { /* silent */ }
    }
    fetchCounts()
    const interval = setInterval(fetchCounts, 30000)
    return () => clearInterval(interval)
  }, [])

  const getActivePage = () => {
    const path = location.pathname
    if (path.includes('/equipe')) return 'equipe'
    if (path.includes('/conversas')) return 'conversas'
    if (path.includes('/tarefas')) return 'tarefas'
    if (path.includes('/dashboard-pipeline')) return 'dashboard'
    if (path.includes('/base')) return 'base'
    if (path.includes('/vouchers')) return 'vouchers'
    if (path.includes('/config')) return 'config'
    return 'cotacoes'
  }

  const active = getActivePage()
  const morePages = ['base', 'vouchers', 'dashboard', 'config']
  const moreIsActive = morePages.includes(active)

  const goTo = (route) => {
    setShowMore(false)
    navigate(route)
  }

  return (
    <>
      {showMore && (
        <>
          <div onClick={() => setShowMore(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 299
          }} />
          <div style={{
            position: 'fixed', bottom: 72, right: 8, zIndex: 300,
            background: '#fff', borderRadius: 12, padding: '8px 0',
            boxShadow: '0 4px 24px rgba(0,0,0,0.15)', minWidth: 180
          }}>
            {[
              { icon: BookOpen, label: 'Base', route: '/app/base', key: 'base' },
              { icon: Paperclip, label: 'Vouchers', route: '/app/vouchers', key: 'vouchers' },
              { icon: BarChart3, label: 'Dashboard', route: '/app/dashboard-pipeline', key: 'dashboard' },
              { icon: Settings, label: 'Configurações', route: '/app/config', key: 'config' },
            ].map(({ icon: Icon, label, route, key }) => (
              <button key={key} onClick={() => goTo(route)} style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                padding: '12px 16px', border: 'none', background: active === key ? '#f0fdf4' : 'transparent',
                cursor: 'pointer', fontSize: 14, color: active === key ? '#10b981' : '#374151',
                fontWeight: active === key ? 600 : 400
              }}>
                <Icon size={18} />
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, height: 64,
        backgroundColor: colors.white, borderTop: `1px solid ${colors.border}`,
        display: 'flex', justifyContent: 'space-around', alignItems: 'center',
        zIndex: 200, paddingBottom: 'env(safe-area-inset-bottom)',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.05)', fontFamily: "'Inter', sans-serif"
      }}>
        {[
          { key: 'cotacoes', icon: FileText, label: 'Cotações', route: '/app/dashboard' },
          { key: 'conversas', icon: MessageCircle, label: 'Conversas', route: '/app/conversas' },
          { key: 'equipe', icon: Users, label: 'Equipe', route: '/app/equipe', badge: teamBadge },
          { key: 'tarefas', icon: CheckSquare, label: 'Tarefas', route: '/app/tarefas', badge: taskBadge },
        ].map(({ key, icon: Icon, label, route, badge }) => {
          const isActive = active === key
          return (
            <button key={key} onClick={() => goTo(route)} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              background: 'none', border: 'none', cursor: 'pointer', padding: '6px 12px',
              position: 'relative', minWidth: 56, WebkitTapHighlightColor: 'transparent'
            }}>
              <Icon size={22} color={isActive ? colors.accent : colors.text.muted} strokeWidth={isActive ? 2.5 : 1.8} />
              <span style={{ fontSize: 10, fontWeight: isActive ? 600 : 400, color: isActive ? colors.accent : colors.text.muted }}>{label}</span>
              {badge > 0 && (
                <span style={{
                  position: 'absolute', top: 0, right: 2, minWidth: 16, height: 16,
                  borderRadius: 8, backgroundColor: '#25d366', color: '#fff',
                  fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', padding: '0 4px'
                }}>{badge > 99 ? '99+' : badge}</span>
              )}
            </button>
          )
        })}

        <button onClick={() => setShowMore(!showMore)} style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          background: 'none', border: 'none', cursor: 'pointer', padding: '6px 12px',
          minWidth: 56, WebkitTapHighlightColor: 'transparent'
        }}>
          {showMore
            ? <X size={22} color={colors.accent} strokeWidth={2.5} />
            : <MoreHorizontal size={22} color={moreIsActive ? colors.accent : colors.text.muted} strokeWidth={moreIsActive ? 2.5 : 1.8} />
          }
          <span style={{ fontSize: 10, fontWeight: moreIsActive || showMore ? 600 : 400, color: moreIsActive || showMore ? colors.accent : colors.text.muted }}>Mais</span>
        </button>
      </nav>
    </>
  )
}
